import PitchCanvas from "./PitchCanvas";
import {add, arg, complex, subtract} from "mathjs";
import * as PropTypes from "prop-types";
import React from "react";

/**
 * The name of this component is bad, we should find another one.
 */
class GoalMap extends PitchCanvas {
    constructor(props) {
        super(props);
        this.handlePithLoaded = this.handlePithLoaded.bind(this);
    }

    componentWillReceiveProps(nextProps, nextContext) {
        this.initPitch();
    }

    /**
     * Draws goal path form first player to goal.
     * @param steps {array} The steps of the goal
     * @return {void}
     */
    drawGoalPath = (steps) => {
        for (let i = 1; i < steps.length; i++) {
            const previousStep = steps[i - 1];
            const currentStep = steps[i];

            if (currentStep.isGoal) {
                this.drawPlayerToGoal(previousStep, currentStep);
            } else {
                this.drawPlayerToPlayer(previousStep, currentStep);
            }
        }
    };

    /**
     * Draws goal step from last player to goal.
     * @param player {object} The player who score.
     * @param goal {complex} The coordinate of the goal.
     */
    drawPlayerToGoal(player, goal) {
        const {position: playerPosition, avatar: playerAvatar} = player;
        const {position: goalPosition} = goal;
        const avatarSize = this.defaultAvatarSize();

        this.drawImage(playerPosition, playerAvatar, avatarSize, avatarSize);
        this.drawGoalArrow(playerPosition, goalPosition, {});
        this.drawGoal(goalPosition);
    }

    /**
     * Draws pass from player to another player.
     * @param fromPlayer {object} The player who pass the ball.
     * @param toPlayer {object} The player who receive the ball.
     */
    drawPlayerToPlayer(fromPlayer, toPlayer) {
        const avatarSize = this.defaultAvatarSize();
        const {position: fromPlayerPosition, avatar: fromPlayerAvatar} = fromPlayer;
        const {position: toPlayerPosition} = toPlayer;
        this.drawImage(fromPlayerPosition, fromPlayerAvatar, avatarSize, avatarSize);
        this.drawArrowBetweenPlayers(fromPlayerPosition, toPlayerPosition, {});
    }

    /**
     * Draws arrow between two players.
     * @param a {complex} The coordinate of player who pass the ball.
     * @param b {complex} The coordinate of player who receive the ball.
     * @param config {object} The override of default config.
     */
    drawArrowBetweenPlayers(a, b, config) {
        const avatarSize = this.defaultAvatarSize();

        const theta = arg(subtract(b, a));
        const newA = add(a, complex({abs: 0.70 * avatarSize, arg: theta}));
        const newB = add(b, complex({abs: -0.75 * avatarSize, arg: theta}));

        super.drawDashedArrow(newA, newB, config);
    };

    /**
     * Draws arrow between last player and goal.
     * @param a {complex} The coordinate of last player who score.
     * @param b {complex} The coordinate of the goal.
     * @param config {object} The override of default config.
     */
    drawGoalArrow(a, b, config) {
        const avatarSize = this.defaultAvatarSize();

        const theta = arg(subtract(b, a));
        const newA = add(a, complex({abs: 0.7 * avatarSize, arg: theta}));
        const newB = add(b, complex({abs: -0.5 * avatarSize, arg: theta}));

        super.drawDashedArrow(newA, newB, config);
    }

    /**
     * Draws circle indicates to the goal.
     * @param point {complex} The coordinate of the ball in the goal.
     */
    drawGoal(point) {
        const avatarSize = this.defaultAvatarSize();

        this.drawCircle(point, 0.3 * avatarSize, '#FFFFFF', 2, true);
        this.drawCircle(point, 0.2 * avatarSize, '#F2C94C', 0, true);
    }

    drawImage(point, image, width, height, callback) {
        const avatarSize = this.defaultAvatarSize();
        this.drawCircle(point, 0.57 * avatarSize, '#FFFFFF', 2, true);
        super.drawImage(point, image, width, height, callback);
    }

    /**
     * Renders the player pass in the match.
     * @param pass {object} the pass in the match.
     */
    drawPass(pass) {
        const {a, b, color} = pass;
        const config = {
            strokeStyle: '#2B82F7',
            fillStyle: '#2B82F7',
        };
        this.drawDashedArrow(a, b, config);
    }


    drawShotOnTarget(shot) {
        const {a, q, b, color} = shot;

        const config = {
            strokeStyle: color,
            fillStyle: color,
            line: {
                arrowHead: {
                    length: 64,
                    filled: false,
                    closed: false,
                },
            },
        };

        this.drawFilledCircle(a, 15, config);
        this.drawCurvedArrow(a, q, b, config);
    }

    /**
     * Pith loaded event.
     * @override
     * @return {void}
     */
    handlePithLoaded() {
        const {goals, passes, shotsOnTarget, duels} = this.props;

        if (goals) {
            goals.map((goal) => ({
                steps: goal.steps.map((step) => ({
                    isGoal: step.isGoal ?? false,
                    avatar: step.avatar ?? undefined,
                    position: complex(step.x, step.y)
                }))
            })).forEach((goal) => {
                this.drawGoalPath(goal.steps);
            });
        }

        if (passes) {
            passes.map((pass) => {
                const {from, to} = pass;
                return {
                    a: complex(from.x, from.y),
                    b: complex(to.x, to.y),
                };
            }).forEach((pass) => {
                this.drawPass(pass);
            });
        }

        if (shotsOnTarget) {
            const accurateColor = '#2B82F7';
            const missedColor = '#F96274';

            shotsOnTarget.map((shot) => {
                const {from, to, type, curvature} = shot;
                return {
                    a: complex(from.x, from.y),
                    q: complex((from.x + to.x) / 2 + curvature, (to.y + from.y) / 2 - curvature),
                    b: complex(to.x, to.y),
                    color: type === 'accurate' ? accurateColor : missedColor,
                };
            }).forEach((shot) => {
                this.drawShotOnTarget(shot);
            });
        }

        this.renderChildren();
    }

    renderChildren = () => {
        const {children} = this.props;
        if (children) {
            children.forEach((child) => {
                const {props} = child;
                const {nodeType} = props;

                switch (nodeType) {
                    case 'circle':
                        this.renderCircleNode(props);
                        break;
                    case 'player-position': {
                        this.renderPlayerPositionNode(props);
                        break;
                    }
                    case 'arrow': {
                        break;
                    }
                    case 'line': {
                        break;
                    }
                }
            });
        }
    };


    /**
     * Renders the circle node.
     * @param data {object} The circle node.
     */
    renderCircleNode = (data) => {
        const {x, y, color, radius} = data;
        const config = {strokeStyle: color, fillStyle: color};
        this.drawFilledCircle(complex(x, y), radius, config);
    };

    /**
     * Renders the player position node.
     * @param data {object} The player position node.
     */
    renderPlayerPositionNode = (data) => {
        const {x, y, radius, number, color, backgroundColor} = data;
        const circleConfig = {strokeStyle: backgroundColor, fillStyle: backgroundColor};
        this.drawFilledCircle(complex(x, y), radius, circleConfig);

        const textConfig = {strokeStyle: color, fillStyle: color, font: 'bold 40px Poppins', textAlign: "center", textBaseline: "middle"};
        this.drawText(complex(x, y), number, textConfig);
    };

    /**
     * Returns size of player avatar in pixels.
     * @returns {number}
     */
    defaultAvatarSize = () => 96;

    /**
     * @override
     * @param x {float} The x-coordinate.
     * @returns {float} The x-coordinate after apply offset.
     */
    offsetX = (x) => {
        const {resolution} = this.canvasConfig();
        const {width} = resolution;

        return x + width / 2
    };

    /**
     * @override
     * @param y {float} The y-coordinate.
     * @returns {float} The y-coordinate after apply offset.
     */
    offsetY = (y) => {
        const {resolution} = this.canvasConfig();
        const {height} = resolution;

        return -y + height / 2
    };


    static propTypes = {
        // The array of goals in match.
        goals: PropTypes.array,
        // The array of passes in match.
        passes: PropTypes.array,
    }
}

// TODO Update Nodes.
GoalMap.Node = (props) => <> </>;
GoalMap.Circle = props => <GoalMap.Node {...props} nodeType='circle'/>;
GoalMap.PlayerPosition = props => <GoalMap.Node {...props} nodeType='player-position'/>;


export default GoalMap;
